import { Routes } from '@angular/router';

import { roleGuard } from './core/auth/role.guard';
import { RoleKind } from './core/models/brijjsports.enums';
import { requireCustomer, requireRoot, tenantNotSuspended } from './core/tenant/tenant.guards';

/**
 * Public outlet routes (welcome, wizards, login) skip the authenticated shell
 * via `data.hideShell`. Role-prefixed routes wrap with the AppLayout shell.
 */
export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'welcome' },

  // ── Public ─────────────────────────────────────────────────────────
  {
    path: 'welcome',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/welcome/pages/welcome-page.component').then(m => m.WelcomePageComponent)
  },
  {
    path: 'login',
    data: { hideShell: true },
    loadComponent: () => import('./features/public/login/pages/login-page.component').then(m => m.LoginPageComponent)
  },
  {
    path: 'join',
    data: { hideShell: true },
    loadComponent: () => import('./features/public/join/pages/join-page.component').then(m => m.JoinPageComponent)
  },
  {
    path: 'coach-onboarding',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/coach-onboarding/pages/coach-onboarding-page.component').then(
        m => m.CoachOnboardingPageComponent
      )
  },
  {
    path: 'live/:tenantSlug/:sessionId',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/spectator/pages/spectator-page.component').then(m => m.SpectatorPageComponent)
  },
  {
    path: 'drop-in/:tenantSlug',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/drop-in/pages/drop-in-page.component').then(m => m.DropInPageComponent)
  },

  // ── Platform admin (root tenant only) ──────────────────────────────
  // canMatch (not canActivate) so the admin bundle never even loads for
  // customer tenants — see docs/brijjsports-multi-tenant-architecture.md §7.
  {
    path: 'admin',
    canMatch: [requireRoot],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'tenants' },
      {
        path: 'tenants',
        data: { breadcrumb: 'Tenants' },
        loadComponent: () =>
          import('./features/admin/tenants/pages/admin-tenants-page.component').then(m => m.AdminTenantsPageComponent)
      },
      {
        path: 'tenants/new',
        data: { breadcrumb: 'New tenant' },
        loadComponent: () =>
          import('./features/admin/tenant-form/pages/admin-tenant-form-page.component').then(
            m => m.AdminTenantFormPageComponent
          )
      },
      {
        path: 'tenants/:id',
        data: { breadcrumb: 'Edit tenant' },
        loadComponent: () =>
          import('./features/admin/tenant-form/pages/admin-tenant-form-page.component').then(
            m => m.AdminTenantFormPageComponent
          )
      },
      {
        path: 'dispatch-log',
        data: { breadcrumb: 'Dispatch log' },
        loadComponent: () =>
          import('./features/admin/dispatch-log/pages/admin-dispatch-log-page.component').then(
            m => m.AdminDispatchLogPageComponent
          )
      }
    ]
  },

  // ── Owner ──────────────────────────────────────────────────────────
  {
    path: 'owner',
    canMatch: [requireCustomer],
    canActivate: [tenantNotSuspended, roleGuard(RoleKind.Owner)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { breadcrumb: 'Dashboard' },
        loadComponent: () =>
          import('./features/owner/dashboard/pages/owner-dashboard-page.component').then(
            m => m.OwnerDashboardPageComponent
          )
      },
      {
        path: 'sports',
        data: { breadcrumb: 'Sports' },
        loadComponent: () =>
          import('./features/owner/sports/pages/owner-sports-page.component').then(m => m.OwnerSportsPageComponent)
      },
      {
        path: 'athletes',
        data: { breadcrumb: 'Athletes' },
        loadComponent: () =>
          import('./features/owner/athletes/pages/owner-athletes-page.component').then(
            m => m.OwnerAthletesPageComponent
          )
      },
      {
        path: 'coaches',
        data: { breadcrumb: 'Coaches' },
        loadComponent: () =>
          import('./features/owner/coaches/pages/owner-coaches-page.component').then(m => m.OwnerCoachesPageComponent)
      },
      {
        path: 'parents',
        data: { breadcrumb: 'Parents' },
        loadComponent: () =>
          import('./features/owner/parents/pages/owner-parents-page.component').then(m => m.OwnerParentsPageComponent)
      },
      {
        path: 'registrations',
        data: { breadcrumb: 'Registrations' },
        loadComponent: () =>
          import('./features/owner/registrations/pages/owner-registrations-page.component').then(
            m => m.OwnerRegistrationsPageComponent
          )
      },
      {
        path: 'programs',
        data: { breadcrumb: 'Programs' },
        loadComponent: () =>
          import('./features/owner/programs/pages/owner-programs-page.component').then(
            m => m.OwnerProgramsPageComponent
          )
      },
      {
        path: 'teams',
        data: { breadcrumb: 'Teams' },
        loadComponent: () =>
          import('./features/owner/teams/pages/owner-teams-page.component').then(m => m.OwnerTeamsPageComponent)
      },
      {
        path: 'events',
        data: { breadcrumb: 'Schedule' },
        loadComponent: () =>
          import('./features/owner/events/pages/owner-events-page.component').then(m => m.OwnerEventsPageComponent)
      },
      {
        path: 'compliance',
        data: { breadcrumb: 'Compliance' },
        loadComponent: () =>
          import('./features/owner/compliance/pages/owner-compliance-page.component').then(
            m => m.OwnerCompliancePageComponent
          )
      },
      {
        path: 'facilities',
        data: { breadcrumb: 'Facilities' },
        loadComponent: () =>
          import('./features/owner/facilities/pages/owner-facilities-page.component').then(
            m => m.OwnerFacilitiesPageComponent
          )
      },
      {
        path: 'analytics',
        data: { breadcrumb: 'Analytics' },
        loadComponent: () =>
          import('./features/owner/analytics/pages/owner-analytics-page.component').then(
            m => m.OwnerAnalyticsPageComponent
          )
      },
      {
        path: 'reports',
        data: { breadcrumb: 'Reports' },
        loadComponent: () =>
          import('./features/owner/reports/pages/owner-reports-page.component').then(m => m.OwnerReportsPageComponent)
      },
      {
        path: 'documents',
        data: { breadcrumb: 'Documents' },
        loadComponent: () =>
          import('./features/owner/documents/pages/owner-documents-page.component').then(
            m => m.OwnerDocumentsPageComponent
          )
      },
      {
        path: 'staff',
        data: { breadcrumb: 'Staff' },
        loadComponent: () =>
          import('./features/owner/staff/pages/owner-staff-page.component').then(m => m.OwnerStaffPageComponent)
      },
      {
        path: 'checkin',
        data: { breadcrumb: 'Check-in kiosk' },
        loadComponent: () =>
          import('./features/owner/checkin/pages/owner-checkin-page.component').then(m => m.OwnerCheckinPageComponent)
      },
      {
        path: 'settings',
        data: { breadcrumb: 'Settings' },
        loadComponent: () =>
          import('./features/owner/settings/pages/owner-settings-page.component').then(
            m => m.OwnerSettingsPageComponent
          )
      },
      {
        path: 'pitch-counts',
        data: { breadcrumb: 'Pitch counts' },
        loadComponent: () =>
          import('./features/owner/pitch-counts/pages/owner-pitch-counts-page.component').then(
            m => m.OwnerPitchCountsPageComponent
          )
      },
      {
        path: 'billing',
        data: { breadcrumb: 'Billing' },
        loadComponent: () =>
          import('./features/owner/billing/pages/owner-billing-page.component').then(
            m => m.OwnerBillingPageComponent
          )
      }
    ]
  },

  // ── Coach ──────────────────────────────────────────────────────────
  {
    path: 'coach',
    canMatch: [requireCustomer],
    canActivate: [tenantNotSuspended, roleGuard(RoleKind.Coach)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { breadcrumb: 'Coach dashboard' },
        loadComponent: () =>
          import('./features/coach/dashboard/pages/coach-dashboard-page.component').then(
            m => m.CoachDashboardPageComponent
          )
      },
      {
        path: 'teams',
        data: { breadcrumb: 'Teams' },
        loadComponent: () =>
          import('./features/coach/teams/pages/coach-teams-page.component').then(m => m.CoachTeamsPageComponent)
      },
      {
        path: 'roster',
        data: { breadcrumb: 'Roster' },
        loadComponent: () =>
          import('./features/coach/roster/pages/coach-roster-page.component').then(m => m.CoachRosterPageComponent)
      },
      {
        path: 'roster/:athleteId',
        data: { breadcrumb: 'Roster · Athlete' },
        loadComponent: () =>
          import('./features/coach/athlete-profile/pages/coach-athlete-profile-page.component').then(
            m => m.CoachAthleteProfilePageComponent
          )
      },
      {
        path: 'schedule',
        data: { breadcrumb: 'Schedule' },
        loadComponent: () =>
          import('./features/coach/schedule/pages/coach-schedule-page.component').then(
            m => m.CoachSchedulePageComponent
          )
      },
      {
        path: 'attendance',
        data: { breadcrumb: 'Attendance' },
        loadComponent: () =>
          import('./features/coach/attendance/pages/coach-attendance-page.component').then(
            m => m.CoachAttendancePageComponent
          )
      },
      {
        path: 'scoring',
        data: { breadcrumb: 'Scoring' },
        loadComponent: () =>
          import('./features/coach/scoring/pages/coach-scoring-page.component').then(m => m.CoachScoringPageComponent)
      },
      {
        path: 'scoring/new',
        data: { breadcrumb: 'Scoring · New session' },
        loadComponent: () =>
          import('./features/coach/scoring-host/pages/coach-scoring-host-page.component').then(
            m => m.CoachScoringHostPageComponent
          )
      },
      {
        path: 'scoring/live/:sessionId',
        data: { breadcrumb: 'Scoring · Live' },
        loadComponent: () =>
          import('./features/coach/scoring-live-host/pages/coach-scoring-live-host-page.component').then(
            m => m.CoachScoringLiveHostPageComponent
          )
      },
      {
        path: 'profile',
        data: { breadcrumb: 'My profile' },
        loadComponent: () =>
          import('./features/coach/profile/pages/coach-profile-page.component').then(m => m.CoachProfilePageComponent)
      },
      {
        path: 'drills',
        data: { breadcrumb: 'Drills' },
        loadComponent: () =>
          import('./features/coach/drills/pages/coach-drills-page.component').then(m => m.CoachDrillsPageComponent)
      },
      {
        path: 'practice-plans',
        data: { breadcrumb: 'Practice plans' },
        loadComponent: () =>
          import('./features/coach/practice-plans/pages/coach-practice-plans-page.component').then(
            m => m.CoachPracticePlansPageComponent
          )
      },
      {
        path: 'tournaments',
        data: { breadcrumb: 'Tournaments' },
        loadComponent: () =>
          import('./features/coach/tournaments/pages/coach-tournaments-page.component').then(
            m => m.CoachTournamentsPageComponent
          )
      },
      {
        path: 'skills',
        data: { breadcrumb: 'Skills' },
        loadComponent: () =>
          import('./features/coach/skills/pages/coach-skills-page.component').then(m => m.CoachSkillsPageComponent)
      },
      {
        path: 'lineups',
        data: { breadcrumb: 'Lineups' },
        loadComponent: () =>
          import('./features/coach/lineups/pages/coach-lineups-page.component').then(
            m => m.CoachLineupsPageComponent
          )
      },
      {
        path: 'availability',
        data: { breadcrumb: 'Availability' },
        loadComponent: () =>
          import('./features/coach/availability/pages/coach-availability-page.component').then(
            m => m.CoachAvailabilityPageComponent
          )
      },
      {
        path: 'messages',
        data: { breadcrumb: 'Messages' },
        loadComponent: () =>
          import('./features/coach/messages/pages/coach-messages-page.component').then(
            m => m.CoachMessagesPageComponent
          )
      },
      {
        path: 'brackets',
        data: { breadcrumb: 'Brackets' },
        loadComponent: () =>
          import('./features/coach/brackets/pages/coach-brackets-page.component').then(
            m => m.CoachBracketsPageComponent
          )
      }
    ]
  },

  // ── Parent ─────────────────────────────────────────────────────────
  {
    path: 'parent',
    canMatch: [requireCustomer],
    canActivate: [tenantNotSuspended, roleGuard(RoleKind.Parent)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { breadcrumb: 'Family dashboard' },
        loadComponent: () =>
          import('./features/parent/dashboard/pages/parent-dashboard-page.component').then(
            m => m.ParentDashboardPageComponent
          )
      },
      {
        path: 'children',
        data: { breadcrumb: 'Children' },
        loadComponent: () =>
          import('./features/parent/children/pages/parent-children-page.component').then(
            m => m.ParentChildrenPageComponent
          )
      },
      {
        path: 'schedule',
        data: { breadcrumb: 'Schedule' },
        loadComponent: () =>
          import('./features/parent/schedule/pages/parent-schedule-page.component').then(
            m => m.ParentSchedulePageComponent
          )
      },
      {
        path: 'attendance',
        data: { breadcrumb: 'Attendance' },
        loadComponent: () =>
          import('./features/parent/attendance/pages/parent-attendance-page.component').then(
            m => m.ParentAttendancePageComponent
          )
      },
      {
        path: 'excused',
        data: { breadcrumb: 'Excused absences' },
        loadComponent: () =>
          import('./features/parent/excused/pages/parent-excused-page.component').then(
            m => m.ParentExcusedPageComponent
          )
      },
      {
        path: 'availability',
        data: { breadcrumb: 'Availability' },
        loadComponent: () =>
          import('./features/parent/availability/pages/parent-availability-page.component').then(
            m => m.ParentAvailabilityPageComponent
          )
      },
      {
        path: 'messages',
        data: { breadcrumb: 'Messages' },
        loadComponent: () =>
          import('./features/parent/messages/pages/parent-messages-page.component').then(
            m => m.ParentMessagesPageComponent
          )
      },
      {
        path: 'billing',
        data: { breadcrumb: 'Billing' },
        loadComponent: () =>
          import('./features/parent/billing/pages/parent-billing-page.component').then(
            m => m.ParentBillingPageComponent
          )
      }
    ]
  },

  // ── Athlete ────────────────────────────────────────────────────────
  {
    path: 'athlete',
    canMatch: [requireCustomer],
    canActivate: [tenantNotSuspended, roleGuard(RoleKind.Athlete)],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { breadcrumb: 'My dashboard' },
        loadComponent: () =>
          import('./features/athlete/dashboard/pages/athlete-dashboard-page.component').then(
            m => m.AthleteDashboardPageComponent
          )
      },
      {
        path: 'profile',
        data: { breadcrumb: 'My profile' },
        loadComponent: () =>
          import('./features/athlete/profile/pages/athlete-profile-page.component').then(
            m => m.AthleteProfilePageComponent
          )
      },
      {
        path: 'scores',
        data: { breadcrumb: 'My scores' },
        loadComponent: () =>
          import('./features/athlete/scores/pages/athlete-scores-page.component').then(
            m => m.AthleteScoresPageComponent
          )
      },
      {
        path: 'schedule',
        data: { breadcrumb: 'My schedule' },
        loadComponent: () =>
          import('./features/athlete/schedule/pages/athlete-schedule-page.component').then(
            m => m.AthleteSchedulePageComponent
          )
      },
      {
        path: 'goals',
        data: { breadcrumb: 'My goals' },
        loadComponent: () =>
          import('./features/athlete/goals/pages/athlete-goals-page.component').then(m => m.AthleteGoalsPageComponent)
      },
      {
        path: 'attendance',
        data: { breadcrumb: 'My attendance' },
        loadComponent: () =>
          import('./features/athlete/attendance/pages/athlete-attendance-page.component').then(
            m => m.AthleteAttendancePageComponent
          )
      },
      {
        path: 'journal',
        data: { breadcrumb: 'My journal' },
        loadComponent: () =>
          import('./features/athlete/journal/pages/athlete-journal-page.component').then(
            m => m.AthleteJournalPageComponent
          )
      },
      {
        path: 'availability',
        data: { breadcrumb: 'My availability' },
        loadComponent: () =>
          import('./features/athlete/availability/pages/athlete-availability-page.component').then(
            m => m.AthleteAvailabilityPageComponent
          )
      },
      {
        path: 'messages',
        data: { breadcrumb: 'Messages' },
        loadComponent: () =>
          import('./features/athlete/messages/pages/athlete-messages-page.component').then(
            m => m.AthleteMessagesPageComponent
          )
      }
    ]
  },

  // ── Tenant-type mismatch landing page ──────────────────────────────
  {
    path: 'not-available',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/not-available/pages/not-available-page.component').then(
        m => m.NotAvailablePageComponent
      )
  },

  // ── Suspended-tenant landing page ──────────────────────────────────
  {
    path: 'suspended',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/suspended/pages/suspended-page.component').then(m => m.SuspendedPageComponent)
  },

  // ── 404 ────────────────────────────────────────────────────────────
  {
    path: '**',
    data: { hideShell: true },
    loadComponent: () =>
      import('./features/public/not-found/pages/not-found-page.component').then(m => m.NotFoundPageComponent)
  }
];
